"use client";

import { useEffect, useState } from "react";

interface UsePageDescriptionOptions {
  /** The `pages` description key, e.g. `about-us`, `return-policy`. */
  key: string;
  businessId?: string;
  lang: string;
  /**
   * The text the route already fetched on the server. When present it is
   * rendered on the first pass — so it is in the server HTML search engines
   * read — and nothing is fetched again. Null or absent falls back to fetching
   * in the browser, as these pages always did.
   */
  initialContent?: string | null;
}

interface PageDescription {
  content: string | null;
  isLoading: boolean;
}

interface DescriptionData {
  text: string;
  type: string;
  language: string;
}

/**
 * The rich-text body of a content page (About us, policies, contact) from the
 * description API.
 */
export function usePageDescription({
  key,
  businessId,
  lang,
  initialContent,
}: UsePageDescriptionOptions): PageDescription {
  const [content, setContent] = useState<string | null>(initialContent || null);
  const [isLoading, setIsLoading] = useState(!initialContent);

  useEffect(() => {
    if (initialContent) return;

    const fetchContent = async () => {
      try {
        const API_URL = process.env.NEXT_PUBLIC_API_URL || "http://localhost:8080";
        const effectiveBusinessId =
          businessId ||
          process.env.NEXT_PUBLIC_BUSINESS_ID ||
          process.env.NEXT_PUBLIC_DEFAULT_BUSINESS_ID ||
          "";
        const response = await fetch(
          `${API_URL}/description?businessId=${effectiveBusinessId}&tableName=pages&key=${key}&language=${lang}`
        );

        if (response.ok) {
          const data: DescriptionData = await response.json();
          if (data && data.text && data.type === "richText") {
            setContent(data.text);
          }
        }
      } catch (error) {
        console.error(`Error fetching ${key} content:`, error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchContent();
  }, [key, businessId, lang, initialContent]);

  return { content, isLoading };
}
